"use client";

import { useCallback } from "react";
import type { MutableRefObject } from "react";
import {
  LogLevel,
  MobileModalDebugEvent,
  type PersistentDebugLogger,
  captureMobileModalEnvironment,
} from "@/hooks/telemetry";
import type { TeamDetailsOpenInput } from "@/components/ui/Chero/Chero.types";
import type {
  HomePageInteractionState,
  OpenTeamDetailsInput,
} from "./domain/home-page/types";

type ClickInvestigationLogLocal = (
  message: string,
  payload: Record<string, unknown>,
  level: LogLevel,
) => void;

interface HomePageHeroHandlersLocal {
  handleHeroTeamDeploy: (teamId: string) => void;
  handleHeroTeamDetailsOpen: (input: TeamDetailsOpenInput) => void;
}

interface UseHomePageHeroHandlersInput {
  clickInvestigationLog: ClickInvestigationLogLocal;
  latestInteractionState: MutableRefObject<HomePageInteractionState>;
  modalDebugLogger: PersistentDebugLogger;
  openDeployForTeam: (teamId: string) => void;
  openTeamDetails: (input: OpenTeamDetailsInput) => void;
}

export function useHomePageHeroHandlers({
  clickInvestigationLog,
  latestInteractionState,
  modalDebugLogger,
  openDeployForTeam,
  openTeamDetails,
}: UseHomePageHeroHandlersInput): HomePageHeroHandlersLocal {
  // The Deploy control carries only the centered team's id — no card element
  // reaches this handler, so the open is centre-grow.
  const handleHeroTeamDeploy = useCallback(
    (teamId: string) => {
      openDeployForTeam(teamId);
    },
    [openDeployForTeam],
  );

  // The hero's carousel card click — the landing's one details opener since
  // the Inspect button was removed (2026-09-01). The card supplies its morph
  // source, so the details modal morphs from the clicked card's rect.
  const handleHeroTeamDetailsOpen = useCallback(
    ({ teamId, morphSource }: TeamDetailsOpenInput) => {
      const { showTeamsModal, showTeamDetailsModal, teamDetailsTeamId } =
        latestInteractionState.current;
      modalDebugLogger.logEventFlow({
        name: MobileModalDebugEvent.TeamDetailsHandlerEntered,
        payload: {
          teamId,
          showTeamsModal,
          showTeamDetailsModal,
          currentTeamDetailsTeamId: teamDetailsTeamId,
          environment: captureMobileModalEnvironment(),
        },
      });
      clickInvestigationLog(
        "click-inv:handleHeroTeamDetailsOpen-entered",
        { teamId, teamIdType: typeof teamId },
        LogLevel.Info,
      );
      openTeamDetails({ teamId, morphSource });
    },
    [
      clickInvestigationLog,
      latestInteractionState,
      modalDebugLogger,
      openTeamDetails,
    ],
  );

  return {
    handleHeroTeamDeploy,
    handleHeroTeamDetailsOpen,
  };
}
